import React, { createContext, useContext, useState, useEffect, useMemo, useRef, ReactNode } from 'react';
import { apiService } from '@services/api';
import { UserId, ClientId, UserRole, toUserId } from '@/types';
import logger from '@utils/logger';
import { ROUTES, MESSAGES, SYSTEM_ADMIN_TOKEN_STORAGE_KEY } from '@/constants';
import { useStableCallback } from '@hooks/useStableCallback';
import { PermissionsMap, isAdminRoleFromPermissions, ALL_PERMISSIONS_GRANTED } from '@/constants/permissions';
import { isPublicAuthPath, isNetworkError, resolveUserPermissions } from './authContextHelpers';

export interface IUser {
  _id: UserId;
  name: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  assignedClients: ClientId[];
  onboarding?: { completed?: boolean; completedAt?: string | null; forceShow?: boolean } | null;
  roleId?: {
    _id: string;
    name: string;
    isAdmin?: boolean;
    assignedClients?: IAssignedclient[];
    permissions?: Record<string, boolean>;
    /** Fase 5, ver PLANO_CHECKLIST_QRCODE_PATRIMONIO — usadas junto por isQrOnlyOperationRole. */
    assignedDemandsOnly?: boolean;
    unassignedDemandsOnly?: boolean;
    requireOperatorNameOnQrScan?: boolean;
    /** Simplifica o formulário de criação de demanda no app mobile pra quem só opera em campo
     * (pedido do usuário, 11/09) — sem consumo próprio no web por enquanto. */
    isOperator?: boolean;
    /** Papel padrão criado no signup (Administrador/Gerente/Operador) — não pode ser excluído
     * nem desativado (pedido do usuário, 11/09). */
    isDefaultRole?: boolean;
    /** Acesso à versão web/app mobile (pedido do usuário, 11/09). */
    webAccess?: boolean;
    mobileAccess?: boolean;
  } | null;
  profileImage?: string;
  permissions: PermissionsMap;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
  isSystemAdmin?: boolean; // Indica se é super admin de suporte do banco control
}

/** Forma de erro usada neste arquivo: erros do axios (com `.response`) e erros criados manualmente
 * com `.response`/`.isNetworkError` anexados para que o `useLogin` consiga distinguir os casos. */
type AuthError = Error & {
  code?: string;
  response?: { status?: number; data?: { message?: string } };
  isNetworkError?: boolean;
};

export interface IAssignedclient {
  _id: ClientId;
  name: string;
  address: string;
  city: string;
  state: string;
}

/**
 * Papel restrito à leitura de QR code (Fase 5, ver PLANO_CHECKLIST_QRCODE_PATRIMONIO): as 3 flags
 * marcadas juntas — só pode operar demandas (iniciar/concluir, observação, evidência) pelo fluxo
 * de QR; ao acessar o board normalmente, é só visualização, sem nenhuma ação de indicador nem
 * edição. Espelha `isQrOnlyOperationRole` do backend (requestHelpers.ts) — mesma regra, aplicada
 * aqui só pra decidir o que a UI mostra/permite (a autorização de verdade é sempre no backend).
 */
export const isQrOnlyOperationRole = (user: IUser | null | undefined): boolean =>
  user?.roleId?.requireOperatorNameOnQrScan === true &&
  user?.roleId?.assignedDemandsOnly === true &&
  user?.roleId?.unassignedDemandsOnly === true;

interface AuthContextType {
  user: IUser | null;
  assignedClients: IAssignedclient[];
  login: (email: string, password: string) => Promise<{
    success: boolean;
    requires2FA?: boolean;
    requiresPayment?: boolean;
    requiresPaymentReason?: 'trial_expired' | 'payment_overdue' | 'billing_invoice_required';
  }>;
  /** Retorna true se o caller deve navegar para o app; false se já redirecionou (ex.: system admin → seleção de tenant). */
  verify2FA: (email: string, code: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
  hasPermission: (permission: keyof IUser['permissions']) => boolean;
  hasAccessToClient: (ClientId: ClientId) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [assignedClients, setAssignedclients] = useState<IAssignedclient[]>([]);
  const [_isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [_lastActivity, setLastActivity] = useState<number>(Date.now());
  const inactivityTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  type RoleClientLike = string | { _id?: unknown; id?: unknown; name?: string; address?: string; city?: string; state?: string };

  const extractRoleClients = (roleData: { assignedClients?: RoleClientLike[] } | null | undefined): { ids: ClientId[]; details: IAssignedclient[] } => {
    if (!roleData || !Array.isArray(roleData.assignedClients)) {
      return { ids: [], details: [] };
    }

    const ids: ClientId[] = [];
    const details: IAssignedclient[] = [];

    roleData.assignedClients.forEach((client) => {
      if (!client) return;

      if (typeof client === 'string') {
        ids.push(client as ClientId);
        return;
      }

      const rawId = client._id || client.id || client;
      if (!rawId) return;

      const idString = typeof rawId === 'string' ? rawId : rawId.toString();
      if (!idString) return;

      const id = idString as ClientId;

      ids.push(id);
      details.push({
        _id: id,
        name: client.name || 'Condomínio',
        address: client.address || '',
        city: client.city || '',
        state: client.state || ''
      });
    });

    const uniqueIds = Array.from(new Set(ids)) as ClientId[];
    return { ids: uniqueIds, details };
  };

  // Função para renovar token automaticamente
  const refreshAccessToken = async (): Promise<boolean> => {
    try {
      logger.debug('Renovando access token...');
      const response = await apiService.refreshToken();
      
      if (response.data.success) {
        logger.debug('Token renovado com sucesso');
        return true;
      }
      
      return false;
    } catch (error: unknown) {
      logger.error('Erro ao renovar token:', error);
      // Se o refresh token expirou, fazer logout
      if ((error as AuthError).response?.status === 401) {
        return false;
      }
      return false;
    }
  };

  // Função para iniciar renovação automática de token (a cada 50 minutos)
  const startTokenRefresh = () => {
    // Limpar intervalo anterior se existir
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
    }

    // Renovar token a cada 50 minutos (antes de expirar em 1 hora)
    tokenRefreshIntervalRef.current = setInterval(async () => {
      await refreshAccessToken();
    }, 50 * 60 * 1000); // 50 minutos
  };

  // Função para parar renovação de token
  const stopTokenRefresh = () => {
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }
  };

  // Eventos que indicam atividade do usuário
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'] as const;

  // Referência para o handler de atividade, para permitir remoção correta dos listeners
  const activityHandlerRef = useRef<((event: Event) => void) | null>(null);

  // Função para atualizar última atividade
  const updateLastActivity = () => {
    setLastActivity(Date.now());
  };

  // Função para iniciar monitoramento de inatividade
  const startInactivityMonitoring = () => {
    // Limpar timeout anterior se existir
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    const resetInactivityTimer = () => {
      updateLastActivity();
      
      // Limpar timeout anterior
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }

      // Configurar novo timeout (30 minutos de inatividade)
      inactivityTimeoutRef.current = setTimeout(() => {
        logger.info('Timeout por inatividade - fazendo logout');
        logout();
      }, 30 * 60 * 1000); // 30 minutos
    };
    
    // Se já existirem listeners registrados, removê-los antes de registrar novamente
    if (activityHandlerRef.current) {
      activityEvents.forEach(event => {
        document.removeEventListener(event, activityHandlerRef.current as EventListener, true);
      });
    }

    // Criar um handler estável que referencia resetInactivityTimer
    const activityHandler = () => resetInactivityTimer();
    activityHandlerRef.current = activityHandler;

    // Adicionar listeners para eventos de atividade
    activityEvents.forEach(event => {
      document.addEventListener(event, activityHandler, true);
    });

    // Iniciar timer
    resetInactivityTimer();
  };

  // Função para parar monitoramento de inatividade
  const stopInactivityMonitoring = () => {
    // Remover todos os listeners de atividade usando o mesmo handler registrado
    if (activityHandlerRef.current) {
      activityEvents.forEach(event => {
        document.removeEventListener(event, activityHandlerRef.current as EventListener, true);
      });
      activityHandlerRef.current = null;
    }
    
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  };

  const logout = async () => {
    // Parar renovação de token e monitoramento de inatividade
    stopTokenRefresh();
    stopInactivityMonitoring();
    
    // Notificar backend para limpar cookies HttpOnly
    try {
      await apiService.logout();
    } catch (error) {
      logger.error('Erro ao chamar logout no backend:', error);
    }
    
    setUser(null);
    setAssignedclients([]);
    
    // LIMPEZA DE SEGURANÇA: Remover qualquer token antigo do localStorage/sessionStorage
    // Tokens não devem mais ser armazenados no localStorage (migração para httpOnly)
    const tokenKeys = ['token', 'accessToken', 'refreshToken', 'authToken', SYSTEM_ADMIN_TOKEN_STORAGE_KEY];
    tokenKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (e) {
        // Ignorar erros de localStorage
      }
    });
    
    // Filtros (período, condomínio, categoria, etc.) NÃO são limpos no logout — são preferências
    // não sensíveis do usuário, e devem sobreviver a sair/entrar de novo (ex.: usuário estava no
    // meio de uma análise, precisou sair, e quer continuar de onde parou horas depois).

    // Disparar evento customizado de logout
    window.dispatchEvent(new Event('logout'));
    
    // Redirecionar para landing page
    window.location.href = ROUTES.HOME;
  };

  // Verificar token ao carregar a aplicação
  useEffect(() => {
    // LIMPEZA DE SEGURANÇA: Remover tokens antigos do localStorage na inicialização
    // Tokens não devem mais ser armazenados no localStorage (migração para httpOnly)
    const tokenKeys = ['token', 'accessToken', 'refreshToken', 'authToken'];
    tokenKeys.forEach(key => {
      try {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          logger.debug(`Token antigo removido do localStorage: ${key}`);
        }
        if (sessionStorage.getItem(key)) {
          sessionStorage.removeItem(key);
          logger.debug(`Token antigo removido do sessionStorage: ${key}`);
        }
      } catch (e) {
        // Ignorar erros de localStorage
      }
    });
    
    const verifyToken = async () => {
      // Em rotas claramente públicas, não tentar verificar sessão nem chamar backend
      const path = window.location.pathname;
      const isPublicPath = isPublicAuthPath(path, ROUTES);

      // Disparar evento para banner verificar impersonação após verificação de token
      // Isso garante que o banner seja atualizado após login/verificação de sessão
      if (!isPublicPath) {
        setTimeout(() => {
          window.dispatchEvent(new Event('impersonation:start'));
        }, 500);
      }

      if (isPublicPath) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        logger.debug('Verificando sessão existente via cookies...');
        const response = await apiService.verifyToken();
        logger.debug('Resposta da verificação recebida');
        if (response.data.success) {
            const userData = response.data.user;
            
            // Transformar os dados do usuário para o formato esperado pelo frontend
            const rolePermissions = userData.roleId?.permissions || {};
            const { ids: roleClientIds, details: roleClientDetails } = extractRoleClients(userData.roleId);

            // Determinar se é administrador principal com base na flag isAdmin,
            // com fallback para verificação de todas as permissões (legado)
            const isAdminRole = isAdminRoleFromPermissions(userData.roleId?.isAdmin, rolePermissions);

            const transformedUser: IUser = {
              _id: userData._id,
              name: userData.name,
              email: userData.email,
              role: isAdminRole ? UserRole.ADMIN : UserRole.USER,
              isActive: userData.isActive,
              assignedClients: roleClientIds,
              onboarding: userData.onboarding || null,
              roleId: userData.roleId ? {
                ...userData.roleId,
                assignedClients: roleClientDetails
              } : null,
              profileImage: userData.profileImage,
              // Admin sempre com todas as permissões — ver resolveUserPermissions (achado real
              // 09/09: um "Administrador" sem uma permissão nova no banco ficava sem ela aqui).
              permissions: resolveUserPermissions(isAdminRole, rolePermissions),
              createdAt: new Date(),
              updatedAt: new Date(),
              isSystemAdmin: userData.isSystemAdmin === true
            };
            
          setUser(transformedUser);
          setAssignedclients(roleClientDetails);
          logger.debug('Sessão válida, usuário logado');
          
          // Iniciar monitoramento de inatividade e renovação de token
          startInactivityMonitoring();
          startTokenRefresh();
        } else {
          logger.debug('Sessão inválida');
          setIsAuthenticated(false);
        }
      } catch (error: unknown) {
        // 401 ao verificar sessão sem cookies é esperado na landing page
        if ((error as AuthError)?.response?.status === 401) {
          logger.debug('Sessão não autenticada ao verificar token (401)');
        } else {
          logger.error('Erro ao verificar sessão:', error);
        }
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    verifyToken();
    
    // Cleanup ao desmontar
    return () => {
      stopTokenRefresh();
      stopInactivityMonitoring();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (
    email: string,
    password: string
  ): Promise<{
    success: boolean;
    requires2FA?: boolean;
    requiresPayment?: boolean;
    requiresPaymentReason?: 'trial_expired' | 'payment_overdue' | 'billing_invoice_required';
  }> => {
    try {
      // LIMPEZA DE SEGURANÇA: Remover qualquer token antigo do localStorage
      // Tokens não devem mais ser armazenados no localStorage (migração para httpOnly)
      const tokenKeys = ['token', 'accessToken', 'refreshToken', 'authToken'];
      tokenKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (e) {
          // Ignorar erros de localStorage (pode estar desabilitado)
        }
      });
      
      const response = await apiService.login(email, password);
      
      if (response.data.success) {
        // Verificar se precisa de código 2FA
        if (response.data.data?.requires2FA) {
          logger.debug('System admin login - requer código 2FA');
          return { success: true, requires2FA: true };
        }

        // Verificar se é system admin que precisa escolher tenant
        if (response.data.data?.requiresTenantSelection) {
          logger.debug('System admin login - redirecionando para seleção de tenant');
          window.location.replace(ROUTES.SYSTEM_ADMIN_SELECT);
          return { success: true };
        }

        // Verificar se precisa fazer pagamento (teste expirado ou assinatura em atraso pós-carência)
        if (response.data.requiresPayment === true) {
          const requiresPaymentReason = response.data.requiresPaymentReason as
            | 'trial_expired'
            | 'payment_overdue'
            | 'billing_invoice_required'
            | undefined;
          logger.debug('Requires payment redirect', { requiresPaymentReason });
          return {
            success: true,
            requiresPayment: true,
            ...(requiresPaymentReason ? { requiresPaymentReason } : {}),
          };
        }

        // Segurança máxima: Tokens são gerenciados APENAS via cookies httpOnly pelo backend
        // Não há mais manipulação de tokens no frontend
        const { user: userData } = response.data;
        
        logger.debug('Login bem-sucedido, salvando dados');
        
        // Transformar os dados do usuário para o formato esperado pelo frontend
        const rolePermissions = userData.roleId?.permissions || {};
        const { ids: roleClientIds, details: roleClientDetails } = extractRoleClients(userData.roleId);

        // Determinar se é administrador principal com base na flag isAdmin,
        // com fallback para verificação de todas as permissões (legado)
        const isAdminRole = isAdminRoleFromPermissions(userData.roleId?.isAdmin, rolePermissions);

        const transformedUser: IUser = {
          _id: userData._id,
          name: userData.name,
          email: userData.email,
          role: isAdminRole ? UserRole.ADMIN : UserRole.USER,
          isActive: userData.isActive,
          assignedClients: roleClientIds,
          onboarding: userData.onboarding || null,
          roleId: userData.roleId ? {
            ...userData.roleId,
            assignedClients: roleClientDetails
          } : null,
          profileImage: userData.profileImage,
          // Admin sempre com todas as permissões — ver resolveUserPermissions (achado real
          // 09/09: um "Administrador" sem uma permissão nova no banco ficava sem ela aqui).
          permissions: resolveUserPermissions(isAdminRole, rolePermissions),
          createdAt: new Date(),
          updatedAt: new Date(),
          isSystemAdmin: userData.isSystemAdmin === true
        };
        
        setUser(transformedUser);
        setAssignedclients(roleClientDetails);
        
        setIsAuthenticated(true);
        
        // Atualizar última atividade
        setLastActivity(Date.now());
        
        // Iniciar monitoramento de inatividade e renovação de token
        startInactivityMonitoring();
        startTokenRefresh();
        
        // Disparar evento customizado de login
        window.dispatchEvent(new Event('login'));
        
        logger.debug('Sessão autenticada com cookies HttpOnly');
        return { success: true };
      }
      logger.warn('Login falhou:', response.data.message);
      // Lançar erro com mensagem específica para que o useLogin possa capturar
      const error = new Error(response.data.message || 'Email ou senha inválidos');
      (error as AuthError).response = { status: 401, data: response.data };
      throw error;
    } catch (error: unknown) {
      logger.error('Erro no login:', error);
      const authError = error as AuthError;

      // Verificar se é erro de rede/conexão (backend indisponível, crash, etc.)
      if (isNetworkError(authError)) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3011';
        const isLocalhost = apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1');
        const message = isLocalhost ? MESSAGES.ERRORS.BACKEND_UNAVAILABLE : MESSAGES.ERRORS.NETWORK;
        logger.error(message);
        const networkError = new Error(message);
        (networkError as AuthError).isNetworkError = true;
        throw networkError;
      }

      // Se for erro do axios com response, verificar status e mensagem
      if (authError.response) {
        logger.error('Resposta de erro:', authError.response.data);
        logger.error('Status:', authError.response.status);

        // Status 403 = conta inativa - propagar mensagem específica
        if (authError.response.status === 403) {
          const message = authError.response.data?.message || 'Sua conta está inativa. Entre em contato com o administrador do sistema para reativar sua conta.';
          const specificError = new Error(message);
          (specificError as AuthError).response = authError.response;
          throw specificError;
        }

        // Outros erros com mensagem do backend
        if (authError.response.data?.message) {
          const specificError = new Error(authError.response.data.message);
          (specificError as AuthError).response = authError.response;
          throw specificError;
        }
      }

      // Se o erro já tem mensagem, propagar
      if (authError.message) {
        throw authError;
      }
      
      // Erro genérico como último recurso
      throw new Error('Erro ao fazer login. Tente novamente.');
    }
  };

  const verify2FA = async (email: string, code: string): Promise<boolean> => {
    try {
      const response = await apiService.verify2FA(email, code);
      
      if (response.data.success) {
        // Verificar se é system admin que precisa escolher tenant
        if (response.data.data?.requiresTenantSelection) {
          logger.debug('System admin 2FA verified - redirecionando para seleção de tenant');
          const accessToken = response.data.accessToken as string | undefined;
          if (accessToken) {
            try {
              sessionStorage.setItem(SYSTEM_ADMIN_TOKEN_STORAGE_KEY, accessToken);
            } catch {
              // sessionStorage indisponível
            }
          }
          const saUser = response.data.data?.user as Record<string, unknown> | undefined;
          if (saUser?.email) {
            setUser({
              _id: toUserId(String(saUser._id || '')),
              name: String(saUser.name || 'System Admin'),
              email: String(saUser.email),
              role: UserRole.ADMIN,
              isActive: true,
              isSystemAdmin: true,
              assignedClients: [],
              permissions: ALL_PERMISSIONS_GRANTED,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
            setIsAuthenticated(true);
          }
          window.location.replace(ROUTES.SYSTEM_ADMIN_SELECT);
          return false;
        }

        // Usuário normal: processar dados do usuário
        const { user: userData } = response.data;
        
        logger.debug('2FA verificado, salvando dados');
        
        // Transformar os dados do usuário para o formato esperado pelo frontend
        const rolePermissions = userData.roleId?.permissions || {};
        const { ids: roleClientIds, details: roleClientDetails } = extractRoleClients(userData.roleId);

        const isAdminRole = isAdminRoleFromPermissions(userData.roleId?.isAdmin, rolePermissions);

        const transformedUser: IUser = {
          _id: userData._id,
          name: userData.name,
          email: userData.email,
          role: isAdminRole ? UserRole.ADMIN : UserRole.USER,
          isActive: userData.isActive,
          assignedClients: roleClientIds,
          onboarding: userData.onboarding || null,
          roleId: userData.roleId ? {
            ...userData.roleId,
            assignedClients: roleClientDetails
          } : undefined,
          profileImage: userData.profileImage,
          // Admin sempre com todas as permissões — ver resolveUserPermissions (achado real
          // 09/09: um "Administrador" sem uma permissão nova no banco ficava sem ela aqui).
          permissions: resolveUserPermissions(isAdminRole, rolePermissions),
          createdAt: new Date(),
          updatedAt: new Date()
        };

        setUser(transformedUser);
        setAssignedclients(roleClientDetails);
        setIsAuthenticated(true);
        setLastActivity(Date.now());
        startInactivityMonitoring();
        startTokenRefresh();
        window.dispatchEvent(new Event('login'));
        logger.debug('Sessão autenticada com cookies HttpOnly após 2FA');
        return true;
      }
      
      throw new Error(response.data.message || 'Código 2FA inválido');
    } catch (error: unknown) {
      logger.error('Erro ao verificar 2FA:', error);
      throw error;
    }
  };

  const hasPermission = (permission: keyof IUser['permissions']): boolean => {
    if (!user) return false;
    // Verificar se o usuário tem acesso total (verificar via roleId)
    // Para admin, sempre retornar true
    if (user.role === UserRole.ADMIN) return true;
    if (user.isSystemAdmin === true) return true;
    if (user.roleId?.isAdmin === true) return true;
    
    // Verificar permissões específicas
    return user.permissions[permission] || false;
  };

  const hasAccessToClient = (ClientId: ClientId): boolean => {
    if (!user) return false;
    if (user.role === UserRole.ADMIN) return true;
    return user.assignedClients.includes(ClientId);
  };

  // Função para atualizar os dados do usuário sem recarregar a página
  const refreshUser = async (): Promise<void> => {
    try {
      const response = await apiService.verifyToken();
      if (response.data.success) {
        const userData = response.data.user;
        
        // Transformar os dados do usuário para o formato esperado pelo frontend
        const rolePermissions = userData.roleId?.permissions || {};
        
        // Prioridade: usar assignedClients do usuário (novo sistema centralizado)
        // Fallback: usar assignedClients do role (compatibilidade com dados antigos)
        let userAssignedClientIds: ClientId[] = [];
        let userAssignedClientDetails: IAssignedclient[] = [];
        
        if (userData.assignedClients && Array.isArray(userData.assignedClients) && userData.assignedClients.length > 0) {
          // Usar assignedClients do usuário
          userAssignedClientIds = (userData.assignedClients as RoleClientLike[]).map((client) => {
            let id: string;
            if (typeof client === 'string') {
              id = client;
            } else if (client && typeof client === 'object' && client._id) {
              id = String(client._id);
            } else {
              id = String(client);
            }
            return id as ClientId;
          });

          userAssignedClientDetails = (userData.assignedClients as RoleClientLike[]).map((client) => {
            const id = typeof client === 'string' ? client : (client?._id || client?.id || String(client));
            return {
              _id: id as ClientId,
              name: (typeof client === 'object' && client?.name) || 'Condomínio',
              address: (typeof client === 'object' && client?.address) || '',
              city: (typeof client === 'object' && client?.city) || '',
              state: (typeof client === 'object' && client?.state) || ''
            };
          }).filter((c) => c._id);
        } else {
          // Fallback: usar assignedClients do role (compatibilidade)
          const { ids: roleClientIds, details: roleClientDetails } = extractRoleClients(userData.roleId);
          userAssignedClientIds = roleClientIds;
          userAssignedClientDetails = roleClientDetails;
        }

        // Determinar se é administrador principal com base na flag isAdmin,
        // com fallback para verificação de todas as permissões (legado)
        const isAdminRole = isAdminRoleFromPermissions(userData.roleId?.isAdmin, rolePermissions);

        const transformedUser: IUser = {
          _id: userData._id,
          name: userData.name,
          email: userData.email,
          role: isAdminRole ? UserRole.ADMIN : UserRole.USER,
          isActive: userData.isActive,
          assignedClients: userAssignedClientIds,
          roleId: userData.roleId ? {
            ...userData.roleId,
            assignedClients: userAssignedClientDetails
          } : null,
          profileImage: userData.profileImage,
          // Admin sempre com todas as permissões — ver resolveUserPermissions (achado real
          // 09/09: um "Administrador" sem uma permissão nova no banco ficava sem ela aqui).
          permissions: resolveUserPermissions(isAdminRole, rolePermissions),
          createdAt: new Date(),
          updatedAt: new Date(),
          isSystemAdmin: userData.isSystemAdmin === true
        };
        
        setUser(transformedUser);
        setAssignedclients(userAssignedClientDetails);
        setIsAuthenticated(true);
        logger.debug('Dados do usuário atualizados');
      }
    } catch (error: unknown) {
      logger.error('Erro ao atualizar dados do usuário:', error);
    }
  };

  // Identidade estável entre renders — evita que consumidores que dependam dessas funções em
  // useEffect/useCallback entrem em loop (a lógica interna de cada função continua igual,
  // sempre com os dados mais recentes; só a referência exposta deixa de mudar a cada render).
  const stableLogin = useStableCallback(login);
  const stableVerify2FA = useStableCallback(verify2FA);
  const stableLogout = useStableCallback(logout);
  const stableRefreshUser = useStableCallback(refreshUser);
  const stableHasPermission = useStableCallback(hasPermission);
  const stableHasAccessToClient = useStableCallback(hasAccessToClient);

  const contextValue = useMemo<AuthContextType>(() => ({
    user,
    assignedClients,
    login: stableLogin,
    verify2FA: stableVerify2FA,
    logout: stableLogout,
    refreshUser: stableRefreshUser,
    isLoading,
    hasPermission: stableHasPermission,
    hasAccessToClient: stableHasAccessToClient
  }), [
    user,
    assignedClients,
    isLoading,
    stableLogin,
    stableVerify2FA,
    stableLogout,
    stableRefreshUser,
    stableHasPermission,
    stableHasAccessToClient
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
